import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signupUser } from '../../../redux/slice/authSlice.js';
import { SignupSchema } from './ValidationSchema.js';
import Text from '../../input_fields/Text.jsx';
import Password from '../../input_fields/Password.jsx';
import DatePicker from '../../input_fields/DatePicker.jsx';
import SaveButton from '../../buttons/SaveButton.jsx';
import addUpdateHandlers from '../../../utils/addUpdateHandlers.js';
import PasswordStrengthBar from '../../input_fields/PasswordStrengthBar.js';
import { toast } from 'react-toastify';
import { resendOtp } from '../../../redux/slice/resendOtpSlice.js';
import { checkUsernameTodo } from '../../../redux/slice/CheckUsername.js';
import dayjs from 'dayjs';
import { countryList } from '../../../redux/slice/CountryList.js';
import PhoneInput, {
  formatPhoneNumber,
  formatPhoneNumberIntl,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  parsePhoneNumber
} from 'react-phone-number-input'
import labels from 'react-phone-number-input/locale/en'
import 'react-phone-number-input/style.css'
import { BiHide, BiShow } from 'react-icons/bi';
import { LuRefreshCw } from 'react-icons/lu';
import { CONSENT_UPDATED_EVENT, requestCookieConsentIfNeeded } from '../../../utils/cookieConsent.js';
import { signupOTPSendTodo } from '../../../redux/slice/SignUpOTPSend.js';
const SignupForm = () => {

  const [spinner, setSpinner] = useState(false);
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecked, setUsernameChecked] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const dispatch = useDispatch();
  const countryData = useSelector((state) => state?.countryList?.data);
  const navigate = useNavigate();
  const location = useLocation();

  const CustomPhoneInput = React.useMemo(() => {
    return React.forwardRef((props, ref) => (
      <input
        {...props}
        ref={ref}
        disabled={!selectedCountry}
        style={{
          backgroundColor: !selectedCountry ? '#f3f4f6' : 'white',
          cursor: !selectedCountry ? 'not-allowed' : 'text'
        }}
      />
    ));
  }, [selectedCountry]);

  const [newPswShow, setNewPswShow] = useState(false);
  const pendingAuthRef = useRef(false);
  const togglePassword = () => {
    setNewPswShow(!newPswShow);
  }

  const countryOptions = countryData
    ? countryData.map((country) => ({
      value: country.phonecode,
      label: country.name,
    }))
    : [];

  const initialValues = {
    name: "",
    username: '',
    phonecode: '',
    phone: "",
    email: "",
    date_of_birth: "",
    password: "",
    terms: false
  };

  const { values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched } = useFormik({
    initialValues: initialValues,
    validationSchema: SignupSchema,

    onSubmit: (values, action) => {
      if (!captchaInput.trim()) {
        setCaptchaError('Please enter CAPTCHA');
        return;
      }
      if (captchaInput !== captchaText) {
        setCaptchaError('Captcha Mismatch');
        refreshCaptcha();
        return;
      }
      pendingAuthRef.current = true;
      if (requestCookieConsentIfNeeded()) {
        pendingAuthRef.current = false;
      } else {
        return;
      }
      setSpinner(true);
      let submitValues = { ...values };

      if (values.phone) {
        try {
          const phoneNumber = parsePhoneNumber(values.phone);
          if (phoneNumber) {
            submitValues.phonecode = phoneNumber.countryCallingCode;
            submitValues.phone = phoneNumber.nationalNumber;
          }
        } catch (error) {
          console.error("Error parsing phone number:", error);
        }
      }

      dispatch(signupOTPSendTodo({ email: submitValues.email })).then((res) => signup_res(res.payload, submitValues));
    }
  });

  useEffect(() => {
    const onConsentUpdated = () => {
      if (!pendingAuthRef.current) return;
      pendingAuthRef.current = false;
      handleSubmit();
    };
    window.addEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
    return () => window.removeEventListener(CONSENT_UPDATED_EVENT, onConsentUpdated);
  }, [handleSubmit]);

  const signup_res = (res, submitValues) => {
    if (res?.status == 200 || res?.status == 201) {
      toast.success(res?.data?.message || 'OTP sent successfully', { position: "bottom-right" })
      setSpinner(false)
      setTimeout(() => {
        navigate('/otp-verify', {
          state: {
            email: submitValues.email,
            from: location.pathname,
            signupData: submitValues
          }
        });
      }, 1500);
    } else if (res?.status == 400) {
      setSpinner(false)
      toast.error(res?.data?.message, { position: "bottom-right" })
    } else if (res?.status == 500) {
      setSpinner(false)
      toast.error("Server error!!..", { position: "bottom-right" })
    } else {
      setSpinner(false)
      toast.error("Something went wrong!!..", { position: "bottom-right" })
    }
  }

  const checkUser = () => {
    const username = values.username;
    if (!username || username.trim() === '') {
      setUsernameError('Please enter a username');
      return;
    }

    if (errors.username) {
      setUsernameError(errors.username);
      return;
    }

    setUsernameError(''); // Clear previous error
    dispatch(checkUsernameTodo({ user_name: username })).then((res) => {
      setUsernameChecked(true);
      if (res?.payload?.status == 400) {
        setUsernameError(res?.payload?.data?.message || 'This username has already been taken');
      } else if (res?.payload?.status == 200 || res?.payload?.status == 201) {
        setUsernameError(''); // Clear error if username is available
        toast.success('Username is available', { position: "bottom-right" });
      }
    });
  };

  // Clear username error when username changes
  const handleNameChange = (e) => {
    setFieldValue('name', e.target.value.replace(/[^A-Za-z.\s]/g, ''));
  };

  const handleUsernameChange = (e) => {
    setFieldValue('username', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''));
    setUsernameChecked(false);
    if (usernameError) {
      setUsernameError('');
    }
  };

  // Handle date of birth change - convert dayjs to YYYY-MM-DD format for Formik
  const handleDateChange = (date) => {
    if (date && dayjs.isDayjs(date)) {
      // Convert dayjs to YYYY-MM-DD format for API
      const formattedDate = date.format('YYYY-MM-DD');
      setFieldValue('date_of_birth', formattedDate);
    } else {
      setFieldValue('date_of_birth', '');
    }
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for DatePicker display
  const getDatePickerValue = () => {
    if (values.date_of_birth) {
      const date = dayjs(values.date_of_birth, 'YYYY-MM-DD');
      if (date.isValid()) {
        return date.format('DD/MM/YYYY');
      }
    }
    return '';
  };

  // CAPTCHA FUNCTION 
  function generateCaptchaText(length = 5) {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'
    let text = ''
    for (let i = 0; i < length; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return text
  }
  function drawCaptcha(canvas, text) {
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const w = canvas.width
    const h = canvas.height

    // Background
    ctx.fillStyle = '#f0f4f8'
    ctx.fillRect(0, 0, w, h)

    // Noise lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `hsl(${Math.random() * 360}, 40%, 70%)`
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(Math.random() * w, Math.random() * h)
      ctx.lineTo(Math.random() * w, Math.random() * h)
      ctx.stroke()
    }

    // Noise dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `hsl(${Math.random() * 360}, 40%, 65%)`
      ctx.beginPath()
      ctx.arc(Math.random() * w, Math.random() * h, 1.5, 0, Math.PI * 2)
      ctx.fill()
    }

    // Draw each character with slight rotation
    const fontSize = 28
    ctx.font = `bold ${fontSize}px 'Courier New', monospace`
    ctx.textBaseline = 'middle'
    const startX = 18
    const spacing = (w - 36) / text.length

    for (let i = 0; i < text.length; i++) {
      ctx.save()
      const x = startX + i * spacing
      const y = h / 2 + (Math.random() * 8 - 4)
      const angle = (Math.random() - 0.5) * 0.4
      ctx.translate(x, y)
      ctx.rotate(angle)
      ctx.fillStyle = `hsl(${200 + Math.random() * 60}, 60%, 35%)`
      ctx.fillText(text[i], 0, 0)
      ctx.restore()
    }
  }

  const canvasRef = useRef(null)
  const [captchaText, setCaptchaText] = useState('')
  const [captchaInput, setCaptchaInput] = useState('')
  const [captchaError, setCaptchaError] = useState('')

  const refreshCaptcha = useCallback(() => {
    const newText = generateCaptchaText()
    setCaptchaText(newText)
    setCaptchaInput('')
    drawCaptcha(canvasRef.current, newText)
  }, [])

  useEffect(() => {
    refreshCaptcha()
  }, [refreshCaptcha])

  // Re-draw when captchaText changes and canvas is ready
  useEffect(() => {
    if (canvasRef.current && captchaText) {
      drawCaptcha(canvasRef.current, captchaText)
    }
  }, [captchaText])

  return (
    <>
      <Form onSubmit={(e) => {
        e.preventDefault();
        if (!captchaInput.trim()) {
          setCaptchaError('Please enter CAPTCHA');
          return;
        }
        if (captchaInput !== captchaText) {
          setCaptchaError('Captcha Mismatch');
          refreshCaptcha();
          return;
        }
        setCaptchaError('');
        handleSubmit();
      }}>
        <div className="">
          <div className='container-fluid'>
            <div className="row">
              <div className="col-md-12">
                <h2 className="mb-4 biolink-title-gradient">Create an Account</h2>
              </div>
              <div className="col-md-6">
                <Text
                  label_name={"Full Name"}
                  placeholder=''
                  disabled={false}
                  name='name'
                  value={values.name || ''}
                  onChange={handleNameChange}
                  onBlur={handleBlur}
                  error={errors.name && touched.name ? (<span className='text-danger form_label'>{errors.name}</span>) : null}
                />
              </div>
              <div className="col-md-6">
                {/* <Text
                  label_name={"Username"}
                  placeholder='Enter Username'
                  disabled={false}
                  name='username'
                  value={values.username || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.username && touched.username ? (<span className='text-danger form_label'>{errors.username}</span>) : null}
                /> */}
                <Form.Label className='form_label'>{'Username'}</Form.Label>
                <InputGroup className="mb-3">
                  <Form.Control
                    placeholder=""
                    aria-label="Enter Username"
                    name='username'
                    value={values.username || ''}
                    onChange={handleUsernameChange}
                    onBlur={handleBlur}
                    aria-describedby="basic-addon2"
                    className={`form_input ${usernameError ? 'is-invalid' : ''}`}
                  />
                  <InputGroup.Text id="basic-addon2" style={{ cursor: usernameChecked ? 'not-allowed' : 'pointer', opacity: usernameChecked ? 0.6 : 1, pointerEvents: usernameChecked ? 'none' : 'auto' }} onClick={() => !usernameChecked && checkUser()}>Check Username</InputGroup.Text>
                </InputGroup>
                {(usernameError && (
                  <span className='text-danger form_label' style={{ fontSize: '14px', marginTop: '-10px', display: 'block' }}>
                    {usernameError}
                  </span>
                )) || (errors.username && touched.username && (
                  <span className='text-danger form_label' style={{ fontSize: '14px', marginTop: '-10px', display: 'block' }}>
                    {errors.username}
                  </span>
                ))}
              </div>

              {/* <div className="col-md-6">
                <Form.Label className='form_label'>{'Contact'}</Form.Label>
                <InputGroup className="mb-3">
                  <Form.Select
                    name="phonecode"
                    value={values.phonecode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className="form_input"
                    style={{ maxWidth: '40%' }}
                    // isInvalid={touched.phonecode && !!errors.phonecode}
                  >
                    <option value="">Country</option>
                    {countryOptions.map((option, index) => (
                      <option key={index} value={option.value}>
                        {option.value} - {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control
                    type="number"
                    placeholder="Phone Number"
                    aria-label="Phone Number"
                    name='phone'
                    className='form_input'
                    value={values.phone || ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    // isInvalid={touched.phone && !!errors.phone}
                  />
                </InputGroup>
               <div className='d-flex'>
                 {touched.phonecode && errors.phonecode && (
                  <div className="text-danger form_label" style={{ fontSize: '14px', marginTop: '-10px' }}>
                    {errors.phonecode}
                  </div>
                )}
                {touched.phone && errors.phone && (
                  <div className="text-danger form_label ms-4" style={{ fontSize: '14px', marginTop: '-10px' }}>
                    {errors.phone}
                  </div>
                )}
               </div>
              </div> */}

              <div className="col-md-6">
                <Form.Label className='form_label'>Contact Number</Form.Label>
                <PhoneInput
                  className={`form_input ${touched.phone && errors.phone ? 'is-invalid' : ''}`}
                  placeholder="Enter phone number"
                  international
                  addInternationalOption={true}
                  labels={{ ...labels, ZZ: 'Select Country Code' }}
                  name='phone'
                  value={values.phone || ''}
                  onCountryChange={setSelectedCountry}
                  onChange={(value) => setFieldValue('phone', value)}
                  onBlur={handleBlur}
                  inputComponent={CustomPhoneInput}
                />
                {touched.phone && errors.phone && (
                  <div className="text-danger form_label" style={{ fontSize: '14px', marginTop: '5px' }}>
                    {errors.phone}
                  </div>
                )}
                {values.phone && !isValidPhoneNumber(values.phone) && (
                  <div className="text-danger form_label" style={{ fontSize: '14px', marginTop: '5px' }}>
                    Invalid phone number
                  </div>
                )}
              </div>


              <div className="col-md-6">
                <DatePicker
                  label_name={"Date of Birth"}
                  placeholder='DD/MM/YYYY'
                  name='date_of_birth'
                  value={getDatePickerValue()}
                  onChange={handleDateChange}
                  onBlur={handleBlur}
                  error={errors.date_of_birth && touched.date_of_birth ? (<span className='text-danger form_label'>{errors.date_of_birth}</span>) : null}
                  mandatory={true}
                />
              </div>
              <div className="col-md-6">
                <Text
                  label_name={"Email Address"}
                  placeholder=''
                  disabled={false}
                  name='email'
                  value={values.email || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.email && touched.email ? (<span className='text-danger form_label'>{errors.email}</span>) : null}
                />
              </div>
              {/* <div className="col-md-6"> */}
              {/* <Password
                  label_name={"Password"}
                  placeholder=''
                  disabled={false}
                  name='password'
                  value={values.password || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.password && touched.password ? (<span className='text-danger form_label'>{errors.password}</span>) : null}
                />
                <PasswordStrengthBar password={values.password} /> */}
              {/* </div> */}
              <div className='col-md-6' style={{ position: 'relative' }}>
                <label htmlFor="user_name" className="form-label form_label">Password</label>
                {newPswShow ? <span className="psw_icon" onClick={togglePassword} ><BiShow size={20} style={{ color: "grey" }} /></span> : <span className="psw_icon" onClick={togglePassword} ><BiHide size={20} style={{ color: "#9d9d9d" }} /></span>}
                <input type={newPswShow ? "text" : "password"}
                  name='password'
                  onChange={(e) => {
                    handleChange(e);
                    setFieldTouched('password', true, true);
                  }}
                  onBlur={handleBlur}
                  value={values.password}
                  className='form-control form_input' />
                {errors.password && (touched.password || values.password) ? (<span className='text-danger ' style={{ fontSize: "15px" }} >{errors.password}</span>) : null}
                <PasswordStrengthBar password={values.password} />
              </div>

              <div className="col-md-12 mt-2">
                <Form.Check type="checkbox" id="terms-checkbox">
                  <Form.Check.Input
                    type="checkbox"
                    name="terms"
                    checked={values.terms}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.terms && !!errors.terms}
                    style={{ borderColor: '#b2b4b6', borderWidth: '2px' }}
                  />
                  <Form.Check.Label>
                    <h6 style={{ fontSize: '14px' }}>
                      I agree to the <Link to="/terms-and-conditions" target="_blank" style={{ textDecoration: 'none' }}><span className={touched.terms && errors.terms ? 'text-danger' : 'text-primary'}>Terms & Conditions</span></Link> and <Link to="/privacy-policy" target="_blank" style={{ textDecoration: 'none' }}><span className={touched.terms && errors.terms ? 'text-danger' : 'text-primary'}>Privacy Policy</span></Link>
                    </h6>
                  </Form.Check.Label>
                </Form.Check>
                <Form.Control.Feedback type="invalid">
                  {errors.terms}
                </Form.Control.Feedback>
              </div>

              <div className="col-md-12 login-input-group mt-2">
                <label>CAPTCHA</label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '8px',
                }}>
                  <canvas
                    ref={canvasRef}
                    width={180}
                    height={48}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <button
                    type="button"
                    onClick={refreshCaptcha}
                    title="Refresh CAPTCHA"
                    style={{
                      background: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      padding: '8px',
                      cursor: 'pointer',
                      color: '#6b7280',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <LuRefreshCw size={18} />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Enter CAPTCHA"
                  value={captchaInput}
                  onChange={(e) => {
                    setCaptchaInput(e.target.value)
                    setCaptchaError('')
                  }}
                  autoComplete="off"
                  className="form-control form_input"
                />
                {captchaError && (
                  <span className='text-danger' style={{ fontSize: '12px' }}>{captchaError}</span>
                )}
              </div>

              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3 mb-4 gap-2">
                <div className="signup-info text-center text-sm-start mt-0">
                  <p className='text-secondary mb-0' style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>Already have an account? <Link to='/login' ><span className='text-primary'> LOGIN </span></Link></p>
                </div>
                <div className="w-100 w-sm-auto text-center text-sm-end">
                  <SaveButton
                    spinner={spinner}
                    button_name={'SIGNUP'}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </>
  );
};

export default SignupForm;